import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';

import OpenInNew from '@mui/icons-material/OpenInNew';
import Terminal from '@mui/icons-material/Terminal';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import useTheme from '@mui/material/styles/useTheme';
import Typography from '@mui/material/Typography';

import { useBrandInfo } from '~/hooks/useBrandInfo';
import useHostingInfo from '~/hooks/useHostingInfo';
import useNovaEnabled from '~/hooks/useNovaEnabled';
import { ButtonVariantPropTypes } from '~/types/mui';

import useAlerts from '../Alerts/alertsStore';
import ButtonAlt from '../ButtonAlt';
import LoadingButtonWrapper from '../LoadingButtonWrapper';
import { getTestId } from './utils/helpers';
import { BaseSsoProps } from './utils/types';

/**
 * Props for SSO component
 *
 * @property sso - SSO hook with refetch method
 * @property isDisabled - Whether component is disabled
 * @property siteId - Site ID for SSO operations
 * @property hostingId - Hosting account ID
 * @property siteType - Site type (wordpress, website, etc.)
 */
interface SsoComponentProps extends BaseSsoProps {
  sso: any;
  isDisabled?: boolean;
  siteId?: string | number;
  hostingId?: string | number;
  siteType?: string;
}

/**
 * SSO component that handles authentication and opens SSO in new window
 *
 * @param children - Content to display in the component
 * @param sso - SSO hook with refetch method
 * @param queryParams - Query parameters for SSO endpoint
 * @param ssoQueryParams - Raw query string to append  to the SSO url
 * @param fluid - Whether to take full width
 * @param variant - {('outlined' | 'contained' | 'text' | 'textLink' | 'menuItem' | 'manageConsoleIconButton')}
 * @param disabled - Whether component is disabled
 * @param onAction - Callback when SSO is triggered
 * @param dataTestId - Test ID for testing
 * @param advHostingQuickLink - Enable advanced hosting link
 * @param isDisabled - Alternative disabled prop
 * @param siteId - Site identifier
 * @param hostingId - Hosting account identifier
 * @param siteType - Type of site
 */
const SSOComponent = ({
  children = undefined,
  sso,
  queryParams = {},
  ssoQueryParams = '',
  fluid = false,
  variant = 'outlined',
  disabled = undefined,
  onAction = undefined,
  onPrepareMFEContext = undefined,
  dataTestId = 'sso-link',
  advHostingQuickLink = false,
  isDisabled = false,
  siteId = undefined,
  hostingId = undefined,
  siteType = 'wordpress',
  size = undefined,
  startIcon: customStartIcon = undefined,
  ...props
}: PropsWithChildren<SsoComponentProps>) => {
  const { t } = useTranslation('site', { useSuspense: false });
  const [, { generateAlert }] = useAlerts();
  const theme = useTheme();
  const { isNovaEnabled } = useNovaEnabled();
  const { brandFromJWT: brand } = useBrandInfo();
  const [isPending, setIsPending] = useState(false);

  const { hostingAccount: accountInfo } = useHostingInfo({
    hostingId,
    options: {
      enabled: !!hostingId,
    },
  });

  const { refetch } = sso;

  // Extract explicit data-testid from props if provided
  const explicitTestId = (props as any)['data-testid'] as string | undefined;

  // Generate the appropriate test ID.
  const finalTestId = getTestId(
    explicitTestId,
    variant,
    siteId,
    hostingId,
    dataTestId,
  );

  const redirectToSsoUrl = async (
    loadingPageType: 'site' | 'hosting' = 'hosting',
  ) => {
    const siteSsoLoadingPage =
      brand === 'bluehost'
        ? `${process.env.PUBLIC_URL}/loading/${siteType}-${brand}.html`
        : `${process.env.PUBLIC_URL}/loading/${siteType}.html`;
    const cPanelSsoLoadingPage = `${process.env.PUBLIC_URL}/sso.html`;

    const ssoWindow = window.open(
      loadingPageType === 'site' ? siteSsoLoadingPage : cPanelSsoLoadingPage,
      '_blank',
    );

    if (!ssoWindow) return;

    setIsPending(true);

    try {
      const { data, error }: any = await refetch();

      if (error) {
        const userMissing = error?.response?.data?.details?.userMissing;
        if (userMissing) {
          if (onAction) {
            onAction('userMissing');
          }
          ssoWindow?.close();
          return;
        }
        ssoWindow?.close();
        return generateAlert({
          severity: 'error',
          description:
            error?.response?.data?.error === 'wpPluginInactiveOrNotInstalled'
              ? t(`error.pluginInactiveOrNotInstalledAlertError`, {
                  brand: accountInfo?.data?.brand,
                })
              : t('error.alertError'),
          showCloseBtn: true,
        });
      }

      const url = data?.data?.url || data?.data?.sso;

      if (url && !!ssoWindow) {
        ssoWindow.location = url + ssoQueryParams;
        if (onAction) {
          onAction();
        }
        return;
      }

      ssoWindow?.close();
      if (data?.data?.error && onAction) {
        onAction(data.data.error);
      } else {
        generateAlert({
          severity: 'error',
          description: t('ssoLink.notFound'),
          showCloseBtn: true,
        });
      }
    } catch (error) {
      ssoWindow?.close();
      generateAlert({
        severity: 'error',
        description: t('error.alertError'),
        showCloseBtn: true,
      });
    } finally {
      setIsPending(false);
    }
  };

  const handleClick = () => {
    onPrepareMFEContext?.();
    redirectToSsoUrl();
  };

  if (variant === 'manageConsoleIconButton') {
    return (
      <ButtonAlt
        aria-label="ManageConsole"
        onClick={handleClick}
        disabled={isDisabled}
        data-testid={finalTestId}
        size={size}
      >
        <Stack alignItems="center" spacing={1}>
          <Terminal color="action" sx={{ fontSize: 24 }} />
          <Typography variant="body2">{t('manageConsole')}</Typography>
        </Stack>
      </ButtonAlt>
    );
  }

  if (advHostingQuickLink) {
    return (
      <LoadingButtonWrapper
        endIcon={<OpenInNew />}
        loading={isPending}
        variant={variant as ButtonVariantPropTypes}
        color="primary"
        onClick={handleClick}
        disabled={isDisabled}
        data-testid="advancedHosting-WHM-btn"
        size={size}
      >
        {children}
      </LoadingButtonWrapper>
    );
  }

  if (variant === 'menuItem') {
    return (
      <MenuItem
        data-testid={finalTestId}
        onClick={handleClick}
        disabled={isDisabled}
      >
        {isPending ? <CircularProgress size={20} /> : children}
      </MenuItem>
    );
  }

  if (variant === 'textLink') {
    return (
      // @ts-expect-error
      <Link
        target="_blank"
        sx={{
          ...(isNovaEnabled && {
            textDecoration: 'none',
            cursor: 'pointer',
            color: theme.palette.primary.main,
            fontSize: '13px',
          }),
        }}
        rel="noopener noreferrer"
        data-testid={finalTestId}
        component="text"
        onClick={handleClick}
        aria-disabled={isDisabled}
        {...props}
      >
        {children}
      </Link>
    );
  }

  // Extract textTransform from props if provided
  const { textTransform } = props as any;
  const sxProps = {
    width: fluid ? '100%' : undefined,
    ...(textTransform && { textTransform }),
  };

  return (
    <LoadingButtonWrapper
      data-testid={finalTestId}
      loading={isPending}
      variant={variant}
      color="primary"
      sx={sxProps}
      onClick={handleClick}
      disabled={isDisabled}
      startIcon={customStartIcon}
      size={size}
    >
      {children}
    </LoadingButtonWrapper>
  );
};

export default SSOComponent;
